import config from '../config'

const TOKEN_KEY = 'file_download_token'
const REFRESH_THRESHOLD_MINUTES = 5
const REFRESH_INTERVAL_MS = 20 * 60 * 1000
let pending: Promise<string> | null = null

/**
 * 解析 token 剩余有效时长（毫秒），解析失败返回 -1
 * token 结构为 base64url(过期时间Ticks).base64url(HMAC签名)，兼容旧版标准 base64
 */
function getTokenRemainingMs(token: string): number {
  try {
    const payload = (token.split('.')[0] || '').replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    const ticks = Number(atob(padded))
    if (!Number.isFinite(ticks)) return -1
    const expireTime = Math.floor(ticks / 10000) - 62135596800000
    return expireTime - Date.now()
  } catch {
    return -1
  }
}

/**
 * 判断 token 是否已过期
 */
function isTokenExpired(token: string): boolean {
  const remaining = getTokenRemainingMs(token)
  return remaining < 0
}

/**
 * 判断 token 是否已过期或临近过期（剩余不足阈值）
 */
function isTokenExpiringSoon(token: string): boolean {
  const remaining = getTokenRemainingMs(token)
  if (remaining < 0) return true
  return remaining <= REFRESH_THRESHOLD_MINUTES * 60 * 1000
}

/**
 * 按天混淆签名 X-Sign（FNV-1a 变体，与后端 XSignFilter 算法一致）
 */
function getXSign(): string {
  const d = new Date()
  const day = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  const src = `GXFIRE-FILE:${day}:Ft7kQx2V9`
  let h = 2166136261
  for (let i = 0; i < src.length; i++) {
    h = Math.imul(h ^ src.charCodeAt(i), 16777619) >>> 0
  }
  h = Math.imul((h ^ 0x5a3c1d2e) >>> 0, 2246822519) >>> 0
  return h.toString(16).padStart(8, '0')
}

/**
 * 获取文件下载 Token（懒获取 + 并发去重）
 * 优先从 localStorage 读取；不存在、已过期或临近过期则调后端接口获取，并写入 localStorage
 * @param force 是否强制刷新
 */
export async function getFileDownloadToken(force = false): Promise<string> {
  const cached = localStorage.getItem(TOKEN_KEY)
  if (cached && !force && !isTokenExpiringSoon(cached)) return cached

  if (pending) return pending

  pending = fetch(`${config.get('SERVER_URL')}api/file/GetDownloadToken`, {
    headers: { 'X-Sign': getXSign() },
  })
    .then((r) => {
      const contentType = r.headers.get('content-type') || ''
      if (!contentType.includes('application/json')) {
        throw new Error(`获取文件下载 Token 失败：期望 JSON，实际返回 ${contentType}`)
      }
      return r.json()
    })
    .then((data) => {
      pending = null
      if (data && (data.code === 0 || data.successful === true)) {
        // 兼容后端返回：新版本 token 在 data，旧版本在 msg
        const token = data.data || data.msg
        if (token) {
          localStorage.setItem(TOKEN_KEY, token)
          return token as string
        }
      }
      throw new Error('获取文件下载 Token 失败')
    })
    .catch((e) => {
      pending = null
      throw e
    })

  return pending
}

/**
 * 清空 Token（用于 401 刷新场景）
 */
export function clearFileDownloadToken() {
  localStorage.removeItem(TOKEN_KEY)
  pending = null
}

/**
 * 同步从 localStorage 读取 Token（不触发获取）
 * 用于在 URL 拼接等同步场景
 */
export function getCachedFileDownloadToken(): string {
  return localStorage.getItem(TOKEN_KEY) || ''
}

/**
 * 定时刷新 Token，保证 <img> 直链等场景拿到的始终是新鲜 Token
 * 各包各自打包一份本模块，用 window 全局标记去重，避免重复注册定时器
 */
if (typeof window !== 'undefined') {
  const g = window as unknown as { __fileDownloadTokenTimer__?: boolean }
  if (!g.__fileDownloadTokenTimer__) {
    g.__fileDownloadTokenTimer__ = true
    setInterval(() => {
      getFileDownloadToken(true).catch(() => {
        // 刷新失败保留旧 token，axios 401 拦截器会兜底重试
      })
    }, REFRESH_INTERVAL_MS)
  }
}
